<template>
  <teleport to="body">
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="edit-outpost-title">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black bg-opacity-70" @click="emitClose"></div>

      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 transform translate-y-3 scale-95"
        enter-to-class="opacity-100 transform translate-y-0 scale-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 transform translate-y-0 scale-100"
        leave-to-class="opacity-0 transform translate-y-2 scale-95"
      >
        <div
          v-if="visible"
          ref="panelRef"
          class="relative w-full max-w-3xl rounded-2xl border border-gray-700 bg-gray-900 text-white shadow-2xl outline-none"
          tabindex="-1"
        >
          <!-- Header -->
          <div class="flex items-start justify-between gap-3 p-4 border-b border-gray-800">
            <div class="space-y-0.5">
              <h2 id="edit-outpost-title" class="text-lg font-semibold flex items-center gap-2">
                <i class="fa-solid fa-pen-to-square text-emerald-400"></i>
                แก้ไข Outpost
              </h2>
              <div class="text-xs text-gray-400 break-all">ID: <span class="font-mono">{{ outpostId }}</span></div>
            </div>
            <button @click="emitClose" class="p-2 rounded-xl hover:bg-gray-800 border border-transparent hover:border-gray-700 transition" aria-label="Close">
              <i class="fa-solid fa-xmark text-gray-300"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="p-4 h-96 overflow-auto" id="style-1">
            <form @submit.prevent="save">
              <!-- Tab Bar -->
              <div class="flex border-b border-gray-800 mb-4">
                <button
                  type="button"
                  @click="currentTab = 'basic'"
                  :class="{'border-b-2 border-emerald-500 text-emerald-400': currentTab === 'basic', 'text-gray-300': currentTab !== 'basic'}"
                  class="px-4 py-2 font-medium text-sm focus:outline-none"
                >
                  ข้อมูลพื้นฐาน
                </button>
                <button
                  type="button"
                  @click="currentTab = 'loot'"
                  :class="{'border-b-2 border-emerald-500 text-emerald-400': currentTab === 'loot', 'text-gray-300': currentTab !== 'loot'}"
                  class="px-4 py-2 font-medium text-sm focus:outline-none"
                >
                  สมบัติ
                </button>
                <button
                  type="button"
                  @click="currentTab = 'defenders'"
                  :class="{'border-b-2 border-emerald-500 text-emerald-400': currentTab === 'defenders', 'text-gray-300': currentTab !== 'defenders'}"
                  class="px-4 py-2 font-medium text-sm focus:outline-none"
                >
                  NPC ป้องกัน
                </button>
              </div>

              <!-- Tab Content -->
              <div v-if="currentTab === 'basic'" class="grid grid-cols-1 md:grid-cols-5 gap-4">
                <!-- Left -->
                <div class="md:col-span-3 space-y-4">
                  <div>
                    <label class="block text-sm text-gray-300 mb-1">ชื่อด่าน (name) <span class="text-red-400">*</span></label>
                    <input v-model.trim="form.name" type="text" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" required />
                  </div>

                  <div>
                    <label class="block text-sm text-gray-300 mb-1">สถานที่ตั้ง (location) <span class="text-red-400">*</span></label>
                    <select v-model="form.location" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" required>
                      <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
                    </select>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-sm text-gray-300 mb-1">เจ้าของ (owner)</label>
                      <input v-model.trim="form.ownerUid" type="text" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" />
                    </div>
                    <div>
                      <label class="block text-sm text-gray-300 mb-1">เลเวล (level)</label>
                      <input v-model.number="form.level" type="number" min="1" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" />
                    </div>
                  </div>

                  <!-- Occupation Fields -->
                  <div>
                    <label class="block text-sm text-gray-300 mb-1">UID ผู้ยึดครองปัจจุบัน (occupierUid)</label>
                    <input
                      v-model.trim="form.occupierUid"
                      type="text"
                      placeholder="UID ของผู้เล่น (เว้นว่างหากไม่มี)"
                      class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-sm text-gray-300 mb-1">รางวัลรายวันสำหรับผู้ยึดครอง (เพชร) (dailyDiamondReward)</label>
                    <input
                      v-model.number="form.dailyDiamondReward"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-sm text-gray-300 mb-1">คะแนนยึดครอง (occupationScore)</label>
                    <input
                      v-model.number="form.occupationScore"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none"
                    />
                  </div>

                  <div>
                    <label class="block text-sm text-gray-300 mb-1">คำอธิบาย (description)</label>
                    <textarea v-model.trim="form.description" rows="3" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none resize-y"></textarea>
                  </div>

                  <div class="flex items-center gap-4">
                    <label class="inline-flex items-center gap-2">
                      <input type="checkbox" v-model="form.isActive" class="rounded bg-gray-800 border-gray-700" />
                      <span class="text-sm text-gray-300">เปิดใช้งาน</span>
                    </label>
                    <label class="inline-flex items-center gap-2">
                      <input type="checkbox" v-model="form.canCapture" class="rounded bg-gray-800 border-gray-700" />
                      <span class="text-sm text-gray-300">สามารถยึดได้</span>
                    </label>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-sm text-gray-300 mb-1">จำนวนชาวบ้านป้องกันสูงสุด (maxDefenderVillagers)</label>
                      <input v-model.number="form.maxDefenderVillagers" type="number" min="1" max="10" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" />
                    </div>
                    <div>
                      <label class="block text-sm text-gray-300 mb-1">จำนวนชาวบ้านบุกสูงสุด (maxAttackerVillagers)</label>
                      <input v-model.number="form.maxAttackerVillagers" type="number" min="1" max="10" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" />
                    </div>
                  </div>
                </div>

                <!-- Right -->
                <div class="md:col-span-2 space-y-4">
                  <div>
                    <label class="block text-sm text-gray-300 mb-1">ลิงก์รูป Outpost (imageUrl)</label>
                    <input v-model.trim="form.imageUrl" type="url" class="w-full rounded-xl bg-gray-800 border border-gray-700 focus:border-emerald-500 px-3 py-2 outline-none" placeholder="https://..." />
                  </div>

                  <!-- Preview -->
                  <div class="rounded-2xl overflow-hidden border border-gray-700 bg-gray-800">
                    <div class="relative" style="padding-top: 56.25%;">
                      <img
                        v-if="form.imageUrl && !imgBroken"
                        :src="form.imageUrl"
                        class="absolute inset-0 w-full h-full object-cover"
                        @error="imgBroken = true"
                      />
                      <div v-else class="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">ตัวอย่างรูปจะแสดงที่นี่</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loot Tab -->
              <div v-if="currentTab === 'loot'" class="space-y-4">
                <div>
                  <label class="block text-sm text-gray-300 mb-1">สมบัติ (lootItems)</label>
                  <p class="text-xs text-gray-400 mb-2">โอกาส 1-10000 (1000 = 10%)</p>
                  <div v-for="(loot, i) in form.lootItems" :key="i" class="flex items-center gap-2 mb-2 p-3 bg-gray-800 rounded-xl border border-gray-700">
                    <input v-model.trim="loot.item" placeholder="ชื่อไอเทม" class="flex-1 rounded-lg bg-gray-700 border border-gray-600 focus:border-emerald-500 px-3 py-1.5 outline-none text-sm" />
                    <input v-model.number="loot.chance" type="number" min="1" max="10000" placeholder="1000" class="w-24 rounded-lg bg-gray-700 border border-gray-600 focus:border-emerald-500 px-3 py-1.5 outline-none text-sm" />
                    <button type="button" @click="moveLootUp(i)" :disabled="i === 0" class="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg text-xs disabled:opacity-50">ขึ้น</button>
                    <button type="button" @click="moveLootDown(i)" :disabled="i === form.lootItems.length - 1" class="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg text-xs disabled:opacity-50">ลง</button>
                    <button type="button" @click="removeLoot(i)" class="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-xs">ลบ</button>
                  </div>
                  <button type="button" @click="addLoot" class="mt-2 text-xs px-2 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700">+ เพิ่ม</button>
                </div>
              </div>

              <!-- Defenders Tab -->
              <div v-if="currentTab === 'defenders'" class="space-y-4">
                <div>
                  <label class="block text-sm text-gray-300 mb-1">NPC ป้องกัน (defenders)</label>
                  <p class="text-xs text-gray-400 mb-2">สูงสุด 10 ชื่อ</p>
                  <div v-for="(npc, i) in form.defenders" :key="i" class="flex items-center gap-2 mb-2 p-3 bg-gray-800 rounded-xl border border-gray-700">
                    <input v-model.trim="form.defenders[i]" placeholder="ชื่อ NPC" class="flex-1 rounded-lg bg-gray-700 border border-gray-600 focus:border-emerald-500 px-3 py-1.5 outline-none text-sm" />
                    <button type="button" @click="removeDefender(i)" class="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-xs">ลบ</button>
                  </div>
                  <button type="button" @click="addDefender" :disabled="form.defenders.length >= 10" class="mt-2 text-xs px-2 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">+ เพิ่ม</button>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center gap-3 pt-4">
                <button type="submit" :disabled="saving || deleting" class="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60">
                  {{ saving ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข' }}
                </button>
                <button type="button" @click="deleteOutpost" :disabled="saving || deleting" class="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60">
                  {{ deleting ? 'กำลังลบ…' : 'ลบ Outpost' }}
                </button>
                <button type="button" @click="emitClose" class="px-3 py-2 rounded-xl border border-gray-700 hover:bg-gray-800">ยกเลิก</button>
                <span v-if="message" :class="messageType === 'error' ? 'text-red-400' : 'text-emerald-400'">{{ message }}</span>
              </div>
            </form>
          </div>
        </div>
      </transition>
    </div>
  </teleport>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { projectFirestore } from '@/firebase/config'

export default {
  name: 'EditOutpostModal',
  props: { outpostId: { type: String, required: true } },
  emits: ['close'],
  setup (props, { emit }) {
    const visible = ref(true)
    const panelRef = ref(null)
    const imgBroken = ref(false)

    const saving = ref(false)
    const deleting = ref(false)
    const message = ref('')
    const messageType = ref('success')

    const currentTab = ref('basic')

    const form = ref({
      name: '',
      ownerUid: '',
      level: 1,
      occupierUid: '',
      occupationScore: 0,
      dailyDiamondReward: 0,
      description: '',
      imageUrl: '',
      isActive: true,
      location: '',
      lootItems: [],
      defenders: [],
      canCapture: false,
      maxDefenderVillagers: 5,
      maxAttackerVillagers: 5
    })

    const locations = [
      'รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล', 'สยามสแควร์', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
      'สถานีดับเพลิงร้าง', 'ค่ายทหาร',
      'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ',
      'โรงเก็บศพจักรกล', 'ตึกปฏิบัติการพันธุกรรม','กรุงเทพ'
    ]

    let stop = null
    let prevOverflow = ''

    const load = (id) => {
      if (stop) stop()
      const docRef = projectFirestore.collection('outposts').doc(id)
      stop = docRef.onSnapshot(
        snap => {
          if (!snap.exists) return
          const d = snap.data()
          form.value = {
            name: d.name || '',
            ownerUid: d.ownerUid || '',
            level: Number(d.level) || 1,
            occupierUid: d.occupierUid || '',
            occupationScore: Number(d.occupationScore) || 0,
            dailyDiamondReward: Number(d.dailyDiamondReward) || 0,
            description: d.description || '',
            imageUrl: d.imageUrl || '',
            isActive: !!d.isActive,
            location: d.location || '',
            lootItems: Array.isArray(d.lootItems) ? d.lootItems.map(x => ({ item: x.item || '', chance: Number(x.chance) || 0 })) : [],
            defenders: Array.isArray(d.defenders) ? d.defenders.map(x => String(x || '')) : [],
            canCapture: !!d.canCapture,
            maxDefenderVillagers: Number(d.maxDefenderVillagers) || 5,
            maxAttackerVillagers: Number(d.maxAttackerVillagers) || 5
          }
        },
        err => {
          message.value = 'โหลดข้อมูลไม่สำเร็จ'
          messageType.value = 'error'
          console.error(err)
        }
      )
    }

    const emitClose = () => {
      visible.value = false
      setTimeout(() => emit('close'), 120)
    }

    const onKey = (e) => { if (e.key === 'Escape') emitClose() }

    const addLoot = () => form.value.lootItems.push({ item: '', chance: 1000 })
    const removeLoot = (i) => form.value.lootItems.splice(i, 1)
    const moveLootUp = (i) => {
      if (i > 0) {
        const temp = form.value.lootItems[i]
        form.value.lootItems.splice(i, 1)
        form.value.lootItems.splice(i - 1, 0, temp)
      }
    }
    const moveLootDown = (i) => {
      if (i < form.value.lootItems.length - 1) {
        const temp = form.value.lootItems[i]
        form.value.lootItems.splice(i, 1)
        form.value.lootItems.splice(i + 1, 0, temp)
      }
    }
    const addDefender = () => { if (form.value.defenders.length < 10) form.value.defenders.push('') }
    const removeDefender = (i) => form.value.defenders.splice(i, 1)

    const save = async () => {
      message.value = ''
      messageType.value = 'success'
      if (!form.value.name?.trim() || !form.value.location?.trim()) {
        message.value = 'กรุณากรอกชื่อด่านและสถานที่ตั้ง'
        messageType.value = 'error'
        return
      }

      const cleanedLoot = form.value.lootItems
        .map(l => ({ item: l.item?.trim() || '', chance: Number(l.chance) || 0 }))
        .filter(l => l.item && l.chance >= 1 && l.chance <= 10000)

      const cleanedDef = form.value.defenders
        .map(d => d?.trim() || '')
        .filter(Boolean)
        .slice(0, 10)

      const payload = {
        name: form.value.name.trim(),
        ownerUid: form.value.ownerUid?.trim() || '',
        level: Number(form.value.level) || 1,
        occupierUid: form.value.occupierUid?.trim() || null,
        occupationScore: Number(form.value.occupationScore) || 0,
        dailyDiamondReward: Number(form.value.dailyDiamondReward) || 0,
        description: form.value.description?.trim() || '',
        imageUrl: form.value.imageUrl?.trim() || '',
        isActive: !!form.value.isActive,
        location: form.value.location.trim(),
        lootItems: cleanedLoot,
        defenders: cleanedDef,
        canCapture: !!form.value.canCapture,
        maxDefenderVillagers: Number(form.value.maxDefenderVillagers) || 5,
        maxAttackerVillagers: Number(form.value.maxAttackerVillagers) || 5,
        updatedAt: new Date()
      }

      try {
        saving.value = true
        await projectFirestore.collection('outposts').doc(props.outpostId).update(payload)

        // อัปเดต outpostPrivate
        const privateDocRef = projectFirestore.collection('outpostPrivate').doc(props.outpostId)
        const privateSnap = await privateDocRef.get()
        if (privateSnap.exists) {
          await privateDocRef.update({ ownerUid: payload.ownerUid })
        } else {
          await privateDocRef.set({ ownerUid: payload.ownerUid })
        }

        message.value = 'บันทึกสำเร็จ'
        messageType.value = 'success'
      } catch (e) {
        console.error(e)
        message.value = 'บันทึกไม่สำเร็จ โปรดลองอีกครั้ง'
        messageType.value = 'error'
      } finally {
        saving.value = false
      }
    }

    const deleteOutpost = async () => {
      if (!confirm('แน่ใจว่าต้องการลบ Outpost นี้หรือไม่? การกระทำนี้ไม่สามารถย้อนกลับได้')) return

      message.value = ''
      messageType.value = 'success'

      try {
        deleting.value = true
        await projectFirestore.collection('outposts').doc(props.outpostId).delete()

        const privateDocRef = projectFirestore.collection('outpostPrivate').doc(props.outpostId)
        const privateSnap = await privateDocRef.get()
        if (privateSnap.exists) {
          await privateDocRef.delete()
        }

        message.value = 'ลบสำเร็จ'
        messageType.value = 'success'
        emitClose()
      } catch (e) {
        console.error(e)
        message.value = 'ลบไม่สำเร็จ โปรดลองอีกครั้ง'
        messageType.value = 'error'
      } finally {
        deleting.value = false
      }
    }

    onMounted(() => {
      load(props.outpostId)
      document.addEventListener('keydown', onKey)
      prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      requestAnimationFrame(() => panelRef.value?.focus?.())
    })

    watch(() => props.outpostId, (id) => id && load(id))

    onBeforeUnmount(() => {
      if (stop) stop()
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow || ''
    })

    return {
      visible, panelRef, imgBroken,
      form, locations,
      saving, deleting, message, messageType,
      currentTab,
      addLoot, removeLoot, moveLootUp, moveLootDown, addDefender, removeDefender,
      save, deleteOutpost, emitClose
    }
  }
}
</script>

<style scoped>
/* ใช้ Tailwind v2 เป็นหลัก */
</style>